/* eslint-disable react-hooks/exhaustive-deps */
//UsersPage.js
import { useEffect, useState, useContext } from "react";
import styled from "styled-components";
import { Sidebar } from "../components/Sidebar";
import Swal from "sweetalert2";
import { AuthContext } from "../context/AuthContext";
import { API_BASE_URL } from "../config";
import { useNavigate } from "react-router-dom";
import { MdError, MdInfo, MdCheckCircle, MdMemory } from "react-icons/md";

export const UsersPage = () => {
  // ---- CONFIG DE COLUNAS (id estável + rótulo) ----
  const COLS = [
    { key: "id", label: "ID" },
    { key: "email", label: "Email" },
    { key: "bucketUser", label: "Bucket e Usuário" },
    { key: "cpfcnpj", label: "CPF/CNPJ" },
    { key: "accesses", label: "Acessos" },
    { key: "permission", label: "Permissão" },
    { key: "plan", label: "Plano" },
    { key: "backupType", label: "Tipo de backup" },
    {
      key: "sysStatus",
      label: "Status do software backup do sistema (pode ter atrasos)",
    },
    {
      key: "fileStatus",
      label: "Status do software backup de arquivos (pode ter atrasos)",
    },
    { key: "password", label: "Senha" },
    { key: "actions", label: "Ações" },
  ];

  // ---- Preferência de colunas escondidas (localStorage) ----
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try {
      const raw = localStorage.getItem("usersPage.hiddenColumns");
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(arr);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(
        "usersPage.hiddenColumns",
        JSON.stringify(Array.from(hiddenColumns)),
      );
    } catch {}
  }, [hiddenColumns]);

  const isVisible = (key) => !hiddenColumns.has(key);
  const hideColumn = (key) =>
    setHiddenColumns((prev) => new Set([...prev, key]));
  const restoreColumns = () => setHiddenColumns(new Set());

  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [planSys, setPlanSys] = useState("");
  const [planFiles, setPlanFiles] = useState("");
  const [permission, setPermission] = useState("");
  const [email, setEmail] = useState("");
  const [cpfcnpj, setCpfCnpj] = useState("");
  const [loading, setLoading] = useState(true);
  const { token } = useContext(AuthContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedUserConfig, setSelectedUserConfig] = useState(null);

  const [showNotifModal, setShowNotifModal] = useState(false);
  const [notifList, setNotifList] = useState([]);
  const [notifUserEmail, setNotifUserEmail] = useState("");

  // === NOVOS STATES PARA LOGS ===
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logsUserEmail, setLogsUserEmail] = useState("");
  const [logsData, setLogsData] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showDatabaseModal, setShowDatabaseModal] = useState(false);
  const [selectedDatabaseUser, setSelectedDatabaseUser] = useState(null);
  const [newDatabaseType, setNewDatabaseType] = useState("PostgreSQL");

  const [currentPage, setCurrentPage] = useState(1);
  // normaliza CPF/CNPJ para só dígitos
  const onlyDigits = (s) => (s || "").replace(/\D/g, "");

  // exibe CPF/CNPJ sempre com máscara
  const formatCpfCnpj = (value) => {
    const d = onlyDigits(String(value || ""));
    if (d.length === 11) {
      // CPF
      return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    if (d.length === 14) {
      // CNPJ
      return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }
    // se não bate 11/14, devolve original (ou d)
    return value || "";
  };

  // paginação passa a usar filteredUsers
  const usersPerPage = 5;
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;

  const [searchEmail, setSearchEmail] = useState("");
  const [searchCpfCnpj, setSearchCpfCnpj] = useState("");

  const [backupType, setBackupType] = useState("");
  const { permissionLevel } = useContext(AuthContext);

  const filteredUsers = users.filter((u) => {
    const matchEmail =
      !searchEmail ||
      String(u.EMAIL || "")
        .toLowerCase()
        .includes(searchEmail.toLowerCase());

    const matchDoc =
      !searchCpfCnpj ||
      onlyDigits(u.CPFCNPJ).includes(onlyDigits(searchCpfCnpj));

    return matchEmail && matchDoc;
  });

  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  // sempre que mudar filtro, volta para página 1
  useEffect(() => {
    setCurrentPage(1);
  }, [searchEmail, searchCpfCnpj]);

  useEffect(() => {
    document.title = "Usuários";
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/list_users_v2.php`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = await res.json();
      if (data.usuarios) {
        const usuariosComDownload = await Promise.all(
          data.usuarios.map(async (user) => {
            const [baixado, backupDatabases] = await Promise.all([
              fetchDownloadStatus(user.USER_ID),
              fetchBackupDatabases(user.USER_ID),
            ]);

            return {
              ...user,
              DOWNLOAD_DONE: baixado,
              BACKUP_DATABASES_TOTAL: backupDatabases.total,
              BACKUP_DATABASES: backupDatabases.dados,
            };
          }),
        );
        setUsers(usuariosComDownload);
      } else {
        Swal.fire({
          title: "Erro",
          text: "Erro ao carregar usuários",
          icon: "error",
          theme: "dark",
        });
      }
    } catch (err) {
      Swal.fire({
        title: "Erro",
        text: "Erro ao buscar usuários",
        icon: "error",
        theme: "dark",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBackupDatabases = async (userId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/list_backup_databases.php?user_id=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await res.json();

      if (data.sucesso) {
        return {
          total: data.total || 0,
          dados: Array.isArray(data.dados) ? data.dados : [],
        };
      }

      return {
        total: 0,
        dados: [],
      };
    } catch (err) {
      console.error("Erro ao buscar bases configuradas:", err);
      return {
        total: 0,
        dados: [],
      };
    }
  };

  const fetchDownloadStatus = async (userId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/list_download_status_by_id.php?user_id=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = await res.json();
      return data.baixado;
    } catch (err) {
      console.error("Erro ao verificar status de download:", err);
      return false;
    }
  };

  const fetchUpdaterStatus = async (userId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/list_updater_status.php?user_id=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await res.json();

      if (
        !data?.sucesso ||
        !Array.isArray(data.dados) ||
        data.dados.length === 0
      ) {
        return {
          UPDATER_IS_RUNNING: false,
          UPDATER_MESSAGE:
            "Updater não está rodando (é atualizado de 20 em 20 minutos)",
        };
      }

      const now = new Date();
      const limiteMs = 20 * 60 * 1000;

      const registroValido = data.dados.find((item) => {
        if (!item?.CREATED_AT) return false;

        const createdAt = new Date(String(item.CREATED_AT).replace(" ", "T"));
        const diff = now - createdAt;

        return diff >= 0 && diff <= limiteMs;
      });

      if (!registroValido) {
        return {
          UPDATER_IS_RUNNING: false,
          UPDATER_MESSAGE:
            "Updater não está rodando (é atualizado de 20 em 20 minutos)",
        };
      }

      if (String(registroValido.STATUS || "").toLowerCase() === "rodando") {
        return {
          UPDATER_IS_RUNNING: true,
          UPDATER_MESSAGE: registroValido.MESSAGE || "Updater rodando",
        };
      }

      return {
        UPDATER_IS_RUNNING: false,
        UPDATER_MESSAGE:
          "Updater não está rodando (é atualizado de 20 em 20 minutos)",
      };
    } catch (err) {
      console.error("Erro ao verificar status do updater:", err);
      return {
        UPDATER_IS_RUNNING: false,
        UPDATER_MESSAGE:
          "Updater não está rodando (é atualizado de 20 em 20 minutos)",
      };
    }
  };

  const fetchServiceStatus = async (user) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/service_status.php?user_id=${user.USER_ID}&service_name=BCSBackupSystem&secret=pKdZZQIsjOFb`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await res.json();

      if (!res.ok || !data.sucesso || !data.status) {
        return {
          SERVICE_IS_RUNNING: false,
          SERVICE_MESSAGE:
            data.erro || "Status do serviço ainda não encontrado.",
        };
      }

      return {
        SERVICE_IS_RUNNING: Number(data.status.IS_RUNNING) === 1,
        SERVICE_MESSAGE:
          Number(data.status.IS_RUNNING) === 1
            ? "Serviço Windows BCSBackupSystem rodando"
            : "Serviço Windows BCSBackupSystem parado",
      };
    } catch (err) {
      console.error("Erro ao verificar status do serviço:", err);

      return {
        SERVICE_IS_RUNNING: false,
        SERVICE_MESSAGE: "Não foi possível verificar o serviço.",
      };
    }
  };

  const handleDownload = async () => {
    window.open(
      "https://bcsapp.com.br/alterdata_backupsapp/apirest/instalador/BCSBackup-Setup.exe?time=" +
        Date.now(),
      "_blank",
    );
  };

  const openDatabaseModal = (user) => {
    setSelectedDatabaseUser(user);
    setNewDatabaseType("PostgreSQL");
    setShowDatabaseModal(true);
  };

  const handleCreateDatabase = async () => {
    if (!selectedDatabaseUser?.USER_ID) return;

    const confirm = await Swal.fire({
      title: "Cadastrar nova base de dados?",
      html: `
      <div style="text-align:left">
        <p>Esta ação irá cadastrar uma nova base de dados para este usuário.</p>
        <p>Também será criada automaticamente uma nova pasta no bucket.</p>
        <small>Exemplo: bcir_backups_database_2/</small>
      </div>
    `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, cadastrar",
      cancelButtonText: "Cancelar",
      theme: "dark",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/create_backup_database.php`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: selectedDatabaseUser.USER_ID,
            database_type: newDatabaseType,
          }),
        },
      );

      const rawText = await res.text();

      console.log("Resposta create_backup_database:", {
        status: res.status,
        ok: res.ok,
        rawText,
      });

      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch (parseErr) {
        throw new Error(
          `Resposta inválida do servidor.\nHTTP ${res.status}\n\n${rawText}`,
        );
      }

      if (res.ok && data.sucesso) {
        Swal.fire({
          title: "Sucesso",
          html: `
          <div style="text-align:left">
            <p>${data.mensagem}</p>
            <p><strong>Pasta criada:</strong></p>
            <pre style="background:#111;padding:10px;border-radius:6px;">${data.folder}</pre>
          </div>
        `,
          icon: "success",
          theme: "dark",
        });

        console.log("Enviando cadastro de base:", {
          user_id: selectedDatabaseUser.USER_ID,
          database_type: newDatabaseType,
        });

        const backupDatabases = await fetchBackupDatabases(
          selectedDatabaseUser.USER_ID,
        );

        const updatedUser = {
          ...selectedDatabaseUser,
          BACKUP_DATABASES_TOTAL: backupDatabases.total,
          BACKUP_DATABASES: backupDatabases.dados,
        };

        setSelectedDatabaseUser(updatedUser);

        setUsers((prev) =>
          prev.map((u) =>
            u.USER_ID === selectedDatabaseUser.USER_ID ? updatedUser : u,
          ),
        );
      } else {
        Swal.fire({
          title: "Erro ao cadastrar base",
          html: `
                  <div style="text-align:left">
                    <p><strong>HTTP:</strong> ${res.status}</p>
                    <p><strong>Erro:</strong></p>
                    <pre style="white-space:pre-wrap;background:#111;padding:10px;border-radius:6px;">${data.erro || "Erro desconhecido"}</pre>
                    ${
                      data.detalhes
                        ? `<p><strong>Detalhes:</strong></p><pre style="white-space:pre-wrap;background:#111;padding:10px;border-radius:6px;">${data.detalhes}</pre>`
                        : ""
                    }
                    <p><strong>Resposta bruta:</strong></p>
                    <pre style="white-space:pre-wrap;background:#111;padding:10px;border-radius:6px;">${rawText}</pre>
                  </div>
                `,
          icon: "error",
          theme: "dark",
        });
      }
    } catch (err) {
      console.error("Erro ao cadastrar base de dados:", err);

      Swal.fire({
        title: "Erro de conexão com o servidor",
        html: `
      <div style="text-align:left">
        <p><strong>Mensagem:</strong></p>
        <pre style="white-space:pre-wrap;background:#111;padding:10px;border-radius:6px;">${String(err?.message || err)}</pre>
      </div>
    `,
        icon: "error",
        theme: "dark",
      });
    }
  };

  const handleDeleteBackupDatabase = async (db) => {
    const confirm = await Swal.fire({
      title: "Excluir base de dados?",
      html: `
      <div style="text-align:left">
        <p>Esta ação irá excluir o registro desta base de dados.</p>
        <p><strong>Pasta:</strong> ${db.BACKUP_FOLDER || "-"}</p>
        <small>A pasta no bucket também será excluída se estiver vazia.</small>
      </div>
    `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      theme: "dark",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/delete_backup_database.php`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id_backup_database: db.ID_BACKUP_DATABASE,
          }),
        },
      );

      const data = await res.json();

      if (data.sucesso) {
        Swal.fire({
          title: "Sucesso",
          text: data.mensagem,
          icon: "success",
          theme: "dark",
        });

        const backupDatabases = await fetchBackupDatabases(
          selectedDatabaseUser.USER_ID,
        );

        const updatedUser = {
          ...selectedDatabaseUser,
          BACKUP_DATABASES_TOTAL: backupDatabases.total,
          BACKUP_DATABASES: backupDatabases.dados,
        };

        setSelectedDatabaseUser(updatedUser);

        setUsers((prev) =>
          prev.map((u) =>
            u.USER_ID === selectedDatabaseUser.USER_ID ? updatedUser : u,
          ),
        );
      } else {
        Swal.fire({
          title: "Erro",
          text: data.erro || "Erro ao excluir base.",
          icon: "error",
          theme: "dark",
        });
      }
    } catch (err) {
      Swal.fire({
        title: "Erro",
        text: "Erro de conexão com o servidor.",
        icon: "error",
        theme: "dark",
      });
    }
  };

  const handleChangeSystemPlan = async (user, db) => {
    const isMainDatabase =
      Number(db?.IS_MAIN_DATABASE) === 1 ||
      db?.BACKUP_FOLDER === "bcir_backups/";

    const { value: plano } = await Swal.fire({
      title: "Alterar Plano (Sistema)",
      input: "select",
      inputOptions: {
        "10GB": "10GB",
        "30GB": "30GB",
        "50GB": "50GB",
        "100GB": "100GB",
      },
      inputValue: isMainDatabase ? user.STORAGE_PLAN : db.STORAGE_PLAN,
      showCancelButton: true,
      confirmButtonText: "Salvar",
      cancelButtonText: "Cancelar",
      theme: "dark",
    });

    if (!plano) return;

    let resp;

    if (isMainDatabase) {
      resp = await updateUserFields(token, user.USER_ID, {
        storage_plan: plano,
      });
    } else {
      resp = await updateDatabasePlan(db.ID_BACKUP_DATABASE, plano);
    }

    if (resp.sucesso) {
      const backupDatabases = await fetchBackupDatabases(user.USER_ID);

      const updatedUser = {
        ...user,
        STORAGE_PLAN: isMainDatabase ? plano : user.STORAGE_PLAN,
        BACKUP_DATABASES_TOTAL: backupDatabases.total,
        BACKUP_DATABASES: backupDatabases.dados,
      };

      setSelectedDatabaseUser(updatedUser);

      setUsers((prev) =>
        prev.map((u) => (u.USER_ID === user.USER_ID ? updatedUser : u)),
      );

      Swal.fire({
        title: "Sucesso",
        text: "Plano atualizado!",
        icon: "success",
        theme: "dark",
      });
    } else {
      Swal.fire({
        title: "Erro",
        text: resp.erro || "Falha ao atualizar",
        icon: "error",
        theme: "dark",
      });
    }
  };

  const handleBackupOptions = async (user) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/list_backup_database_settings.php?user_id=${user.USER_ID}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await res.json();

      if (!data.sucesso) {
        Swal.fire({
          title: "Erro",
          text: data.erro || "Erro ao buscar configurações das bases.",
          icon: "error",
          theme: "dark",
        });
        return;
      }

      setSelectedUserConfig({
        userId: user.USER_ID,
        userEmail: user.EMAIL,
        databases: data.dados || [],
      });

      setShowModal(true);
    } catch (err) {
      Swal.fire({
        title: "Erro",
        text: "Erro ao buscar configurações das bases.",
        icon: "error",
        theme: "dark",
      });
    }
  };

  useEffect(() => {
    if (loading) return;
    if (currentUsers.length === 0) return;

    updateAllSoftwareStatus();

    const interval = setInterval(() => {
      updateAllSoftwareStatus();
    }, 15000);

    return () => clearInterval(interval);
  }, [
    loading,
    currentPage,
    searchEmail,
    searchCpfCnpj,
    token,
    currentUsers.length,
  ]);

  const updateAllSoftwareStatus = async () => {
    const resultados = await Promise.allSettled(
      currentUsers.map(async (user) => {
        const [sysRes, filesRes, memRes, updaterRes, serviceRes] =
          await Promise.allSettled([
            fetch(
              `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/list_software_status.php?user_id=${user.USER_ID}`,
              { headers: { Authorization: `Bearer ${token}` } },
            ),
            fetch(
              `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/list_software_status_backup_files.php?user_id=${user.USER_ID}`,
              { headers: { Authorization: `Bearer ${token}` } },
            ),
            fetch(
              `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/list_software_memory_rss.php?user_id=${user.USER_ID}`,
              { headers: { Authorization: `Bearer ${token}` } },
            ),
            fetchUpdaterStatus(user.USER_ID),
            fetchServiceStatus(user),
          ]);

        const sys =
          sysRes.status === "fulfilled" && sysRes.value.ok
            ? await sysRes.value.json()
            : null;

        const files =
          filesRes.status === "fulfilled" && filesRes.value.ok
            ? await filesRes.value.json()
            : null;

        const mem =
          memRes.status === "fulfilled" && memRes.value.ok
            ? await memRes.value.json()
            : null;

        const updater =
          updaterRes.status === "fulfilled"
            ? updaterRes.value
            : {
                UPDATER_IS_RUNNING: false,
                UPDATER_MESSAGE:
                  "Updater não está rodando (é atualizado de 20 em 20 minutos)",
              };

        const service =
          serviceRes.status === "fulfilled"
            ? serviceRes.value
            : {
                SERVICE_IS_RUNNING: false,
                SERVICE_MESSAGE: "Não foi possível verificar o serviço.",
              };

        return {
          userId: user.USER_ID,
          patch: {
            SOFTWARE_STATUS: sys?.mensagem ?? "Software não respondeu",
            SOFTWARE_RUNNING: sys?.status === "running",

            SOFTWARE_FBACKUP_STATUS:
              files?.mensagem ?? "Software não respondeu",
            SOFTWARE_FBACKUP_RUNNING: files?.status === "running",

            SOFTWARE_SYS_PRIVATE_WS_BYTES: mem?.found
              ? Number(mem?.private_working_set_bytes ?? 0)
              : null,
            SOFTWARE_SYS_PRIVATE_WS_AGE_SEC: mem?.found
              ? Number(mem?.age_sec ?? null)
              : null,
            SOFTWARE_SYS_RSS_BYTES: mem?.found
              ? Number(mem?.rss_bytes || 0)
              : null,

            UPDATER_IS_RUNNING: updater.UPDATER_IS_RUNNING,
            UPDATER_MESSAGE: updater.UPDATER_MESSAGE,
            SERVICE_IS_RUNNING: service.SERVICE_IS_RUNNING,
            SERVICE_MESSAGE: service.SERVICE_MESSAGE,
          },
        };
      }),
    );

    setUsers((prev) =>
      prev.map((u) => {
        const item = resultados.find(
          (r) => r.status === "fulfilled" && r.value.userId === u.USER_ID,
        );
        return item ? { ...u, ...item.value.patch } : u;
      }),
    );
  };

  const openNotificationModal = async (user) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/list_all_notifications.php?user_id=${user.USER_ID}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await res.json();
      if (data.notifications) {
        setNotifList(data.notifications);
        setNotifUserEmail(user.EMAIL);
        setShowNotifModal(true);
      } else {
        Swal.fire({
          icon: "info",
          title: "Nenhum alerta",
          text: "Nenhuma notificação encontrada.",
          theme: "dark",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Erro ao buscar notificações.",
        theme: "dark",
      });
    }
  };

  // === NOVA FUNÇÃO: VER LOGS DO USUÁRIO ===
  const openLogsModal = async (user) => {
    try {
      setLogsLoading(true);
      setLogsData(null);
      setLogsUserEmail(user.EMAIL);

      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/list_user_logs.php?user_id=${user.USER_ID}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await res.json();

      if (res.ok && data.logs) {
        // verifica se pelo menos um log não é null
        const hasAny =
          data.logs.watcher_stdout ||
          data.logs.watcher_stderr ||
          data.logs.system_stdout ||
          data.logs.system_stderr;

        if (!hasAny) {
          Swal.fire({
            icon: "info",
            title: "Sem logs",
            text: "Nenhum arquivo de log encontrado para este usuário.",
            theme: "dark",
          });
          return;
        }

        setLogsData(data.logs);
        setShowLogsModal(true);
      } else {
        Swal.fire({
          icon: "info",
          title: "Sem logs",
          text:
            data.mensagem ||
            "Nenhum arquivo de log encontrado para este usuário.",
          theme: "dark",
        });
      }
    } catch (err) {
      console.log(err);

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Erro ao buscar logs do usuário.",
        theme: "dark",
      });
    } finally {
      setLogsLoading(false);
    }
  };

  async function updateDatabasePlan(idBackupDatabase, storagePlan) {
    const res = await fetch(
      `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/update_backup_database_plan.php`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_backup_database: idBackupDatabase,
          storage_plan: storagePlan,
        }),
      },
    );

    return res.json();
  }

  async function updateUserFields(token, userId, payload) {
    const res = await fetch(
      `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/update_user_fields.php`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id: userId, ...payload }),
      },
    );
    return res.json();
  }

  const formatDateInMessage = (msg) => {
    if (!msg) return "";
    // casa 2025-09-12 00:33:08 ou 2025-09-12T00:33:08
    // (global: converte todas as ocorrências)
    return msg.replace(
      /\b(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})\b/g,
      (_, y, m, d, hh, mm, ss) => `${d}/${m}/${y} ${hh}:${mm}:${ss}`,
    );
  };

  const renderIconByType = (type) => {
    switch (type) {
      case 0:
        return <MdError color="#e11d48" size={20} />;
      case 1:
        return <MdCheckCircle color="#22c55e" size={20} />;
      case 2:
      default:
        return <MdInfo color="#3b82f6" size={20} />;
    }
  };

  const handleDeleteUser = async (userId) => {
    const confirm = await Swal.fire({
      title: "Tem certeza?",
      text: "Essa ação excluirá o usuário permanentemente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      theme: "dark",
    });

    if (confirm.isConfirmed) {
      try {
        const res = await fetch(
          `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/delete_user.php`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: `user_id=${userId}`,
          },
        );
        const data = await res.json();
        if (data.sucesso) {
          Swal.fire({
            title: "Sucesso",
            text: data.mensagem,
            icon: "success",
            theme: "dark",
          });
          fetchUsers();
        } else {
          Swal.fire({
            title: "Erro",
            text: data.erro || "Erro ao excluir",
            icon: "error",
            theme: "dark",
          });
        }
      } catch (err) {
        Swal.fire({
          title: "Erro",
          text: "Erro de conexão com o servidor",
          icon: "error",
          theme: "dark",
        });
      }
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!email || !cpfcnpj || !backupType || !permission) {
      Swal.fire({
        title: "Atenção",
        text: "Preencha todos os campos",
        icon: "warning",
        theme: "dark",
      });
      return;
    }

    if (backupType === "1" && !planSys) {
      Swal.fire({
        title: "Atenção",
        text: "Escolha o plano para o backup do sistema",
        icon: "warning",
        theme: "dark",
      });
      return;
    }
    if (backupType === "2" && !planFiles) {
      Swal.fire({
        title: "Atenção",
        text: "Escolha o plano para o backup de arquivos",
        icon: "warning",
        theme: "dark",
      });
      return;
    }
    if (backupType === "3" && (!planSys || !planFiles)) {
      Swal.fire({
        title: "Atenção",
        text: "Escolha o plano para o backup de arquivos e do sistema",
        icon: "warning",
        theme: "dark",
      });
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/create_user.php`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            cpfcnpj,
            storage_plan_sys: planSys || undefined,
            storage_plan_files: planFiles || undefined,
            backupType: backupType,
            permission_level: parseInt(permission, 10),
          }),
        },
      );
      const data = await res.json();

      if (data.sucesso) {
        Swal.fire({
          title: "Usuário criado!",
          html: `
            <div style="text-align:left">
              <p>A senha inicial (mostrada uma única vez) é:</p>
              <pre style="background:#111;padding:10px;border-radius:6px;user-select:all">${data.temp_password}</pre>
              <small>Copie e envie ao cliente. Após fechar esta janela, não será possível visualizar novamente.</small>
            </div>
          `,
          icon: "success",
          confirmButtonText: "Ok",
          theme: "dark",
        });
        setEmail("");
        setCpfCnpj("");
        setPlanSys("");
        setPlanFiles("");
        setBackupType("");
        setPermission("");
        fetchUsers();
      } else {
        Swal.fire({
          title: "Erro",
          text: data.erro || "Erro ao cadastrar",
          icon: "error",
          theme: "dark",
        });
      }
    } catch (err) {
      Swal.fire({
        title: "Erro",
        text: "Erro de conexão com o servidor: " + err,
        icon: "error",
        theme: "dark",
      });
    }
  };

  const handleChangePassword = async (user) => {
    const { value: formValues } = await Swal.fire({
      title: `Alterar senha — ${user.EMAIL}`,
      html:
        '<input type="password" id="swal-pass1" class="swal2-input" placeholder="Nova senha">' +
        '<input type="password" id="swal-pass2" class="swal2-input" placeholder="Confirmar nova senha">',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Salvar",
      cancelButtonText: "Cancelar",
      theme: "dark",
      preConfirm: () => {
        const p1 = document.getElementById("swal-pass1").value || "";
        const p2 = document.getElementById("swal-pass2").value || "";
        if (p1.length < 6) {
          Swal.showValidationMessage(
            "A senha deve ter pelo menos 6 caracteres.",
          );
          return false;
        }
        if (p1 !== p2) {
          Swal.showValidationMessage("As senhas não conferem.");
          return false;
        }
        return { password: p1 };
      },
    });

    if (!formValues) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/change_password.php`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: user.USER_ID,
            new_password: formValues.password,
          }),
        },
      );
      const data = await res.json();
      if (data.sucesso) {
        Swal.fire({
          title: "Sucesso",
          text: "Senha alterada com sucesso.",
          icon: "success",
          theme: "dark",
        });
      } else {
        Swal.fire({
          title: "Erro",
          text: data.erro || "Falha ao alterar a senha.",
          icon: "error",
          theme: "dark",
        });
      }
    } catch (err) {
      Swal.fire({
        title: "Erro",
        text: "Erro de conexão com o servidor.",
        icon: "error",
        theme: "dark",
      });
    }
  };

  const renderPermission = (level) => {
    const lvl = Number(level);
    return lvl === 1 ? "Administrativo" : lvl === 2 ? "Técnico" : "Cliente";
  };

  const renderBackupType = (type) => {
    const numericType = parseInt(type); // <-- garante que é número
    switch (numericType) {
      case 1:
        return "Backup do sistema";
      case 2:
        return "Backup de arquivos";
      case 3:
        return "Backup do sistema e arquivos";
      default:
        return "-";
    }
  };

  // ---- Renderização de cada célula, reaproveitando seu código atual ----
  const renderCell = (key, user) => {
    switch (key) {
      case "id":
        return user.USER_ID;

      case "email": {
        const databases = Array.isArray(user.BACKUP_DATABASES)
          ? user.BACKUP_DATABASES
          : [];

        const total = Number(user.BACKUP_DATABASES_TOTAL || 0);

        const databasesText =
          databases.length > 0
            ? databases.map((db) => db.DATABASE_TYPE).join(", ")
            : "Nenhuma base configurada";

        return (
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <span>{user.EMAIL}</span>

            <span
              title={`Total de bases: ${total}\nBases: ${databasesText}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: "#374151",
                color: "#fff",
                fontSize: "0.75rem",
                fontWeight: "bold",
                cursor: "help",
              }}
            >
              ?
            </span>
          </span>
        );
      }

      case "bucketUser":
        return (
          <div style={{ textAlign: "center" }}>
            <div>{user.BUCKET_NAME + " / " + user.IAM_USERNAME}</div>
            {permissionLevel === 1 && (
              <>
                <button
                  onClick={() =>
                    navigate(
                      `/usuario-bucket?user_id=${encodeURIComponent(
                        user.USER_ID,
                      )}`,
                    )
                  }
                  style={{
                    marginTop: 6,
                    background: "#0ea5e9",
                    color: "white",
                    border: "none",
                    padding: "0.35rem 0.7rem",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                  title="Ver arquivos do bucket deste usuário"
                >
                  Ver bucket
                </button>

                <button
                  onClick={() => openNotificationModal(user)}
                  style={{
                    marginTop: 6,
                    marginLeft: 6,
                    background: "#eab308",
                    color: "black",
                    border: "none",
                    padding: "0.35rem 0.7rem",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                  title="Ver notificações deste usuário"
                >
                  🔔 Notificações
                </button>

                <button
                  onClick={() => openDatabaseModal(user)}
                  style={{
                    marginTop: 6,
                    marginLeft: 6,
                    background: "#16a34a",
                    color: "white",
                    border: "none",
                    padding: "0.35rem 0.7rem",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                  title="Ver bases de dados deste usuário"
                >
                  🗄️ Base de dados
                </button>

                {/* NOVO BOTÃO: VER LOGS */}
                <button
                  onClick={() => openLogsModal(user)}
                  style={{
                    marginTop: 6,
                    marginLeft: 6,
                    background: "#6b7280",
                    color: "white",
                    border: "none",
                    padding: "0.35rem 0.7rem",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                  title="Ver logs do software desktop deste usuário"
                  disabled={logsLoading}
                >
                  📜 Ver logs
                </button>
              </>
            )}
          </div>
        );

      case "cpfcnpj":
        return formatCpfCnpj(user.CPFCNPJ);

      case "accesses":
        return user.ACCESSES;

      case "permission":
        return (
          <>
            {renderPermission(user.PERMISSION_LEVEL)}{" "}
            {permissionLevel !== 2 && (
              <button
                title="Editar permissão"
                style={{
                  marginLeft: 6,
                  background: "transparent",
                  color: "#4faaff",
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={async () => {
                  const { value: perm } = await Swal.fire({
                    title: "Alterar Permissão",
                    input: "select",
                    inputOptions: {
                      1: "Administrativo",
                      2: "Técnico",
                      3: "Cliente",
                    },
                    inputValue: String(user.PERMISSION_LEVEL),
                    showCancelButton: true,
                    confirmButtonText: "Salvar",
                    cancelButtonText: "Cancelar",
                    theme: "dark",
                  });
                  if (perm) {
                    const resp = await updateUserFields(token, user.USER_ID, {
                      permission_level: parseInt(perm, 10),
                    });
                    if (resp.sucesso) {
                      setUsers((prev) =>
                        prev.map((u) =>
                          u.USER_ID === user.USER_ID
                            ? { ...u, PERMISSION_LEVEL: parseInt(perm, 10) }
                            : u,
                        ),
                      );
                      Swal.fire({
                        title: "Sucesso",
                        text: "Permissão atualizada!",
                        icon: "success",
                        theme: "dark",
                      });
                    } else {
                      Swal.fire({
                        title: "Erro",
                        text: resp.erro || "Falha ao atualizar",
                        icon: "error",
                        theme: "dark",
                      });
                    }
                  }
                }}
              >
                ✎
              </button>
            )}
          </>
        );

      case "plan":
        return (
          <>
            {(Number(user.BACKUP_TYPE) === 2 ||
              Number(user.BACKUP_TYPE) === 3) && (
              <>Arquivos: {user.STORAGE_PLAN_BCK_FILES}</>
            )}

            {permissionLevel !== 2 && (
              <>
                {(Number(user.BACKUP_TYPE) === 2 ||
                  Number(user.BACKUP_TYPE) === 3) && (
                  <button
                    title="Editar plano (Arquivos)"
                    style={{
                      marginLeft: 6,
                      background: "transparent",
                      color: "#4faaff",
                      border: "none",
                      cursor: "pointer",
                    }}
                    onClick={async () => {
                      const { value: plano } = await Swal.fire({
                        title: "Alterar Plano (Arquivos)",
                        input: "select",
                        inputOptions: {
                          "100GB": "100GB",
                          "200GB": "200GB",
                          "300GB": "300GB",
                          "400GB": "400GB",
                          "500GB": "500GB",
                          "1000GB": "1000GB",
                        },
                        inputValue: user.STORAGE_PLAN_BCK_FILES,
                        showCancelButton: true,
                        confirmButtonText: "Salvar",
                        cancelButtonText: "Cancelar",
                        theme: "dark",
                      });
                      if (plano) {
                        const resp = await updateUserFields(
                          token,
                          user.USER_ID,
                          { storage_plan_files: plano },
                        );
                        if (resp.sucesso) {
                          setUsers((prev) =>
                            prev.map((u) =>
                              u.USER_ID === user.USER_ID
                                ? { ...u, STORAGE_PLAN_BCK_FILES: plano }
                                : u,
                            ),
                          );
                          Swal.fire({
                            title: "Sucesso",
                            text: "Plano (Arquivos) atualizado!",
                            icon: "success",
                            theme: "dark",
                          });
                        } else {
                          Swal.fire({
                            title: "Erro",
                            text: resp.erro || "Falha ao atualizar",
                            icon: "error",
                            theme: "dark",
                          });
                        }
                      }
                    }}
                  >
                    ✎
                  </button>
                )}
              </>
            )}
          </>
        );

      case "backupType":
        return (
          <>
            <div>{renderBackupType(user.BACKUP_TYPE)}</div>

            <div
              style={{
                marginTop: 6,
                color: user.UPDATER_IS_RUNNING ? "#5bac81" : "#d9534f",
                fontSize: "0.9rem",
                fontWeight: 500,
              }}
            >
              {user.UPDATER_MESSAGE || "Verificando updater..."}
            </div>

            {permissionLevel !== 2 && (
              <button
                title="Editar tipo de backup"
                style={{
                  marginLeft: 6,
                  background: "transparent",
                  color: "#4faaff",
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={async () => {
                  const { value: tipo } = await Swal.fire({
                    title: "Alterar Tipo de Backup",
                    input: "select",
                    inputOptions: {
                      1: "Backup do sistema",
                      2: "Backup de arquivos",
                      3: "Backup do sistema e arquivos",
                    },
                    inputValue: String(user.BACKUP_TYPE),
                    showCancelButton: true,
                    confirmButtonText: "Salvar",
                    cancelButtonText: "Cancelar",
                    theme: "dark",
                  });
                  if (tipo) {
                    const resp = await updateUserFields(token, user.USER_ID, {
                      backup_type: parseInt(tipo, 10),
                    });
                    if (resp.sucesso) {
                      setUsers((prev) =>
                        prev.map((u) =>
                          u.USER_ID === user.USER_ID
                            ? { ...u, BACKUP_TYPE: parseInt(tipo, 10) }
                            : u,
                        ),
                      );
                      Swal.fire({
                        title: "Sucesso",
                        text: "Tipo de backup atualizado!",
                        icon: "success",
                        theme: "dark",
                      });
                    } else {
                      Swal.fire({
                        title: "Erro",
                        text: resp.erro || "Falha ao atualizar",
                        icon: "error",
                        theme: "dark",
                      });
                    }
                  }
                }}
              >
                ✎
              </button>
            )}
          </>
        );

      case "sysStatus": {
        const isDown =
          user.SOFTWARE_STATUS?.includes(
            "Software do backup do sistema não está rodando",
          ) ||
          user.SOFTWARE_STATUS?.includes(
            "Software do backup foi interrompido agora",
          );

        const shouldShowServiceStatus =
          !isDown && typeof user.SOFTWARE_RUNNING === "boolean";

        const statusColor = isDown ? "#d9534f" : "#5bac81";
        const serviceColor = user.SERVICE_IS_RUNNING ? "#5bac81" : "#d9534f";

        const rssBytes = user.SOFTWARE_SYS_RSS_BYTES;
        const rssMb =
          typeof rssBytes === "number"
            ? (rssBytes / 1024 / 1024).toFixed(3) // 3 casas como seu exemplo
            : null;

        const bytes = user.SOFTWARE_SYS_PRIVATE_WS_BYTES;
        const mb =
          typeof bytes === "number" ? (bytes / 1024 / 1024).toFixed(3) : null;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ color: statusColor }}>
              {typeof user.SOFTWARE_RUNNING === "boolean"
                ? user.SOFTWARE_STATUS
                : "Verificando..."}
            </span>

            {shouldShowServiceStatus && (
              <span
                style={{
                  color: serviceColor,
                  fontSize: "0.9rem",
                }}
              >
                {user.SERVICE_MESSAGE || "Verificando serviço Windows..."}
              </span>
            )}

            {rssMb && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#a78bfa", // cor diferente
                  fontSize: "0.85rem",
                }}
                title={
                  typeof user.SOFTWARE_SYS_RSS_AGE_SEC === "number"
                    ? `Atualizado há ${user.SOFTWARE_SYS_RSS_AGE_SEC}s`
                    : "Memória RSS"
                }
              >
                <MdMemory size={16} />
                <span>{mb} Uso de memória</span>
              </div>
            )}
          </div>
        );
      }

      case "fileStatus":
        return (
          <span
            style={{
              color:
                user.SOFTWARE_FBACKUP_STATUS?.includes(
                  "Software do backup de arquivos não está rodando",
                ) ||
                user.SOFTWARE_FBACKUP_STATUS?.includes(
                  "Software do backup foi interrompido agora.",
                )
                  ? "#d9534f"
                  : "#5bac81",
            }}
          >
            {typeof user.SOFTWARE_FBACKUP_RUNNING === "boolean"
              ? user.SOFTWARE_FBACKUP_STATUS
              : "Verificando..."}
          </span>
        );

      case "password":
        return (
          permissionLevel !== 2 && (
            <button
              title="Alterar senha"
              onClick={() => handleChangePassword(user)}
              style={{
                background: "#16a34a",
                color: "white",
                border: "none",
                padding: "0.35rem 0.7rem",
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              Alterar
            </button>
          )
        );

      case "actions":
        return (
          <>
            <button
              onClick={() => handleBackupOptions(user)}
              style={{
                marginRight: "8px",
                background: "#4f46e5",
                color: "white",
                border: "none",
                padding: "0.4rem 0.8rem",
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              Opções de Backup
            </button>
            {Number(user.USER_ID) !== 1 && permissionLevel === 1 && (
              <button
                onClick={() => handleDeleteUser(user.USER_ID)}
                style={{
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  padding: "0.4rem 0.8rem",
                  borderRadius: "6px",
                  cursor: "pointer",
                  marginTop: "7px",
                }}
              >
                Excluir
              </button>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Wrapper>
      <Sidebar />
      <Main>
        <Content>
          <Section>
            <Title>Lista de Usuários</Title>
            <button
              onClick={handleDownload}
              style={{
                marginRight: "8px",
                background: "#4f46e5",
                color: "white",
                border: "none",
                padding: "0.4rem 0.8rem",
                borderRadius: "6px",
                cursor: "pointer",
                marginBottom: "10px",
              }}
            >
              Baixar instalador do software desktop
            </button>
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                marginBottom: "12px",
                flexWrap: "wrap",
              }}
            >
              <input
                type="text"
                placeholder="Filtrar por e-mail"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                style={{
                  background: "#2a2a2a",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "0.6rem 0.8rem",
                  minWidth: 220,
                }}
              />
              <input
                type="text"
                placeholder="Filtrar por CPF/CNPJ"
                value={searchCpfCnpj}
                onChange={(e) => setSearchCpfCnpj(e.target.value)}
                style={{
                  background: "#2a2a2a",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "0.6rem 0.8rem",
                  minWidth: 220,
                }}
              />
              {(searchEmail || searchCpfCnpj) && (
                <button
                  onClick={() => {
                    setSearchEmail("");
                    setSearchCpfCnpj("");
                  }}
                  style={{
                    background: "#666",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    padding: "0.6rem 0.8rem",
                    cursor: "pointer",
                    height: 40,
                  }}
                  title="Limpar filtros"
                >
                  Limpar
                </button>
              )}
              {hiddenColumns.size > 0 && (
                <button
                  onClick={restoreColumns}
                  style={{
                    background: "#0ea5e9",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    padding: "0.6rem 0.8rem",
                    cursor: "pointer",
                    height: 40,
                  }}
                  title="Mostrar novamente todas as colunas escondidas"
                >
                  Restaurar colunas
                </button>
              )}
            </div>
            {loading ? (
              <p>Carregando...</p>
            ) : (
              <TableViewport>
                <Table>
                  <thead>
                    <tr>
                      {COLS.filter((c) => isVisible(c.key)).map((c) => (
                        <th key={c.key}>
                          <span>{c.label}</span>
                          <button
                            onClick={() => hideColumn(c.key)}
                            title="Esconder esta coluna"
                            style={{
                              marginLeft: 8,
                              background: "transparent",
                              color: "#999",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "0.95rem",
                            }}
                            aria-label={`Esconder coluna ${c.label}`}
                          >
                            ❎
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {currentUsers.map((user) => (
                      <tr key={user.USER_ID}>
                        {COLS.filter((c) => isVisible(c.key)).map((c) => (
                          <td key={c.key}>{renderCell(c.key, user)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableViewport>
            )}
            <div style={{ marginTop: "1rem", textAlign: "center" }}>
              {Array.from(
                { length: Math.ceil(users.length / usersPerPage) },
                (_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    style={{
                      margin: "0 5px",
                      padding: "6px 12px",
                      background: currentPage === i + 1 ? "#4f46e5" : "#2a2a2a",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    {i + 1}
                  </button>
                ),
              )}
            </div>
          </Section>

          {showModal && (
            <ModalBackupOptions
              config={selectedUserConfig}
              onClose={() => {
                setShowModal(false);
                fetchUsers();
              }}
              onSaved={fetchUsers}
            />
          )}

          {permissionLevel !== 2 && (
            <Section>
              <Title>Cadastrar Novo Usuário</Title>
              <Form onSubmit={handleCreateUser}>
                <Input
                  type="email"
                  placeholder="E-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Input
                  type="text"
                  placeholder="CPF ou CNPJ"
                  value={cpfcnpj}
                  onChange={(e) => setCpfCnpj(e.target.value)}
                />
                <Select
                  value={backupType}
                  onChange={(e) => setBackupType(e.target.value)}
                >
                  <option value="">Tipo de Backup</option>
                  <option value="1">Backup do sistema</option>
                  <option value="2">Backup de arquivos</option>
                  <option value="3">Backup do sistema e arquivos</option>
                </Select>

                {/* Permissão (igual) */}
                <Select
                  value={permission}
                  onChange={(e) => setPermission(e.target.value)}
                >
                  <option value="">Nível de Permissão</option>
                  <option value="1">Administrativo</option>
                  <option value="2">Técnico</option>
                  <option value="3">Cliente</option>
                </Select>

                {/* Planos dinâmicos */}
                {backupType === "1" && (
                  <Select
                    value={planSys}
                    onChange={(e) => setPlanSys(e.target.value)}
                  >
                    <option value="">Plano (Sistema)</option>
                    <option value="10GB">10GB</option>
                    <option value="30GB">30GB</option>
                    <option value="50GB">50GB</option>
                    <option value="100GB">100GB</option>
                  </Select>
                )}

                {backupType === "2" && (
                  <Select
                    value={planFiles}
                    onChange={(e) => setPlanFiles(e.target.value)}
                  >
                    <option value="">Plano (Arquivos)</option>
                    <option value="100GB">100GB</option>
                    <option value="200GB">200GB</option>
                    <option value="300GB">300GB</option>
                    <option value="400GB">400GB</option>
                    <option value="500GB">500GB</option>
                    <option value="1000GB">1000GB</option>
                  </Select>
                )}

                {backupType === "3" && (
                  <>
                    <Select
                      value={planSys}
                      onChange={(e) => setPlanSys(e.target.value)}
                    >
                      <option value="">Plano (Sistema)</option>
                      <option value="10GB">10GB</option>
                      <option value="30GB">30GB</option>
                      <option value="50GB">50GB</option>
                      <option value="100GB">100GB</option>
                    </Select>
                    <Select
                      value={planFiles}
                      onChange={(e) => setPlanFiles(e.target.value)}
                    >
                      <option value="">Plano (Arquivos)</option>
                      <option value="100GB">100GB</option>
                      <option value="200GB">200GB</option>
                      <option value="300GB">300GB</option>
                      <option value="400GB">400GB</option>
                      <option value="500GB">500GB</option>
                      <option value="1000GB">1000GB</option>
                    </Select>
                  </>
                )}
                <Button type="submit">Cadastrar</Button>
              </Form>
            </Section>
          )}
        </Content>
      </Main>
      {showNotifModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#1a1a1a",
              padding: "2rem",
              borderRadius: "10px",
              width: "90%",
              maxWidth: "600px",
              color: "#fff",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <h2>Notificações — {notifUserEmail}</h2>

            {notifList.length === 0 ? (
              <p style={{ color: "#aaa" }}>Nenhuma notificação encontrada.</p>
            ) : (
              notifList.map((notif) => (
                <div
                  key={notif.ID_NOTIFICATION}
                  style={{
                    borderBottom: "1px solid #333",
                    paddingBottom: "1rem",
                    marginBottom: "1rem",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {renderIconByType(Number(notif.TYPE))}
                    <strong>{notif.TITLE}</strong>
                  </div>

                  <p style={{ marginTop: 6 }}>
                    {formatDateInMessage(notif.MESSAGE)}
                  </p>

                  <small style={{ color: "#888" }}>
                    {new Date(notif.CREATED_AT).toLocaleString("pt-BR")}
                  </small>
                </div>
              ))
            )}

            <div style={{ textAlign: "right" }}>
              <button
                onClick={() => setShowNotifModal(false)}
                style={{
                  marginTop: "1rem",
                  background: "#4f46e5",
                  padding: "0.5rem 1rem",
                  borderRadius: "8px",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {showDatabaseModal && selectedDatabaseUser && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#1a1a1a",
              padding: "2rem",
              borderRadius: "10px",
              width: "90%",
              maxWidth: "fit-content",
              color: "#fff",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <h2>Bases de dados — {selectedDatabaseUser.EMAIL}</h2>

            <p style={{ color: "#ccc", marginTop: 10 }}>
              Total de bases:{" "}
              <strong>
                {selectedDatabaseUser.BACKUP_DATABASES_TOTAL || 0}
              </strong>
            </p>

            <div style={{ marginTop: "1rem" }}>
              <h3 style={{ marginBottom: 8 }}>Bases cadastradas</h3>

              {Array.isArray(selectedDatabaseUser.BACKUP_DATABASES) &&
              selectedDatabaseUser.BACKUP_DATABASES.length > 0 ? (
                selectedDatabaseUser.BACKUP_DATABASES.map((db) => (
                  <div
                    key={db.ID_BACKUP_DATABASE}
                    style={{
                      background: "#111",
                      border: "1px solid #333",
                      borderRadius: 8,
                      padding: "0.75rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "1rem",
                      }}
                    >
                      <div>
                        <strong>{db.DATABASE_TYPE}</strong>
                        <div
                          style={{
                            color: "#aaa",
                            fontSize: "0.85rem",
                            marginTop: 4,
                          }}
                        >
                          Plano atual:{" "}
                          {Number(db.IS_MAIN_DATABASE) === 1
                            ? selectedDatabaseUser.STORAGE_PLAN ||
                              db.STORAGE_PLAN ||
                              "-"
                            : db.STORAGE_PLAN || "-"}
                        </div>
                      </div>

                      <div
                        style={{
                          color: "#aaa",
                          fontSize: "0.85rem",
                          marginTop: 4,
                          minWidth: 160,
                        }}
                      >
                        <div>Pasta: {db.BACKUP_FOLDER || "-"}</div>

                        <div>
                          Base:{" "}
                          {db.DATABASE_NAME
                            ? db.DATABASE_NAME
                            : "Ainda não vinculada no instalador"}
                        </div>

                        {Number(db.INSTALLED) === 1 && db.MACHINE_NAME && (
                          <div>Máquina: {db.MACHINE_NAME}</div>
                        )}
                      </div>

                      <button
                        onClick={() =>
                          handleChangeSystemPlan(selectedDatabaseUser, db)
                        }
                        style={{
                          background: "#4f46e5",
                          color: "white",
                          border: "none",
                          padding: "0.4rem 0.75rem",
                          borderRadius: "6px",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Alterar plano
                      </button>
                      <button
                        onClick={() => handleDeleteBackupDatabase(db)}
                        style={{
                          background: "#dc2626",
                          color: "white",
                          border: "none",
                          padding: "0.4rem 0.75rem",
                          borderRadius: "6px",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p style={{ color: "#aaa" }}>Nenhuma base configurada.</p>
              )}
            </div>

            <div
              style={{
                marginTop: "1.5rem",
                paddingTop: "1.5rem",
                borderTop: "1px solid #333",
              }}
            >
              <h3>Cadastrar nova base de dados?</h3>

              <select
                value={newDatabaseType}
                onChange={(e) => setNewDatabaseType(e.target.value)}
                style={{
                  width: "100%",
                  marginTop: "0.75rem",
                  background: "#2a2a2a",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "0.75rem",
                }}
              >
                <option value="PostgreSQL">PostgreSQL</option>
              </select>

              <button
                onClick={handleCreateDatabase}
                style={{
                  marginTop: "1rem",
                  background: "#16a34a",
                  color: "white",
                  border: "none",
                  padding: "0.55rem 1rem",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Cadastrar
              </button>
            </div>

            <div style={{ textAlign: "right" }}>
              <button
                onClick={() => {
                  setShowDatabaseModal(false);
                  setSelectedDatabaseUser(null);
                }}
                style={{
                  marginTop: "1.5rem",
                  background: "#4f46e5",
                  padding: "0.5rem 1rem",
                  borderRadius: "8px",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogsModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#1a1a1a",
              padding: "2rem",
              borderRadius: "10px",
              width: "90%",
              maxWidth: "900px",
              color: "#fff",
              maxHeight: "80vh",
              overflowY: "auto",
              fontFamily: "monospace",
              fontSize: "0.85rem",
            }}
          >
            <h2>Logs — {logsUserEmail}</h2>

            {!logsData ? (
              <p style={{ color: "#aaa", marginTop: "1rem" }}>
                Carregando logs...
              </p>
            ) : (
              <>
                {/* Cada bloco só aparece se tiver conteúdo */}
                {logsData.system_stdout && (
                  <div style={{ marginTop: "1rem" }}>
                    <h3>system_stdout.log</h3>
                    <pre
                      style={{
                        background: "#111",
                        padding: "0.75rem",
                        borderRadius: "6px",
                        overflowX: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {logsData.system_stdout}
                    </pre>
                  </div>
                )}

                {logsData.system_stderr && (
                  <div style={{ marginTop: "1rem" }}>
                    <h3>system_stderr.log</h3>
                    <pre
                      style={{
                        background: "#111",
                        padding: "0.75rem",
                        borderRadius: "6px",
                        overflowX: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {logsData.system_stderr}
                    </pre>
                  </div>
                )}

                {logsData.watcher_stdout && (
                  <div style={{ marginTop: "1rem" }}>
                    <h3>watcher_stdout.log</h3>
                    <pre
                      style={{
                        background: "#111",
                        padding: "0.75rem",
                        borderRadius: "6px",
                        overflowX: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {logsData.watcher_stdout}
                    </pre>
                  </div>
                )}

                {logsData.watcher_stderr && (
                  <div style={{ marginTop: "1rem" }}>
                    <h3>watcher_stderr.log</h3>
                    <pre
                      style={{
                        background: "#111",
                        padding: "0.75rem",
                        borderRadius: "6px",
                        overflowX: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {logsData.watcher_stderr}
                    </pre>
                  </div>
                )}
              </>
            )}

            <div style={{ textAlign: "right" }}>
              <button
                onClick={() => setShowLogsModal(false)}
                style={{
                  marginTop: "1rem",
                  background: "#4f46e5",
                  padding: "0.5rem 1rem",
                  borderRadius: "8px",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </Wrapper>
  );
};

const ModalEditHour = ({ value, onConfirm, onClose, onSave }) => (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    }}
  >
    <div
      style={{
        background: "#1a1a1a",
        padding: "2rem",
        borderRadius: "10px",
        maxWidth: "400px",
        width: "90%",
        color: "#fff",
      }}
    >
      <h3>Editar Horário</h3>
      <input
        type="time"
        value={value}
        onChange={(e) => onConfirm(e.target.value)}
        style={{
          backgroundColor: "#2a2a2a",
          color: "#fff",
          border: "1px solid #444",
          borderRadius: "6px",
          padding: "0.5rem 0.75rem",
          fontSize: "1rem",
          width: "-webkit-fill-available",
          marginBottom: "1rem",
        }}
      />
      <div style={{ textAlign: "right" }}>
        <button
          onClick={onClose}
          style={{
            marginRight: "0.5rem",
            padding: "0.5rem 1rem",
            border: "none",
            borderRadius: 6,
            background: "#999",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          Cancelar
        </button>
        <button
          onClick={() => {
            onSave(); // ✅ chama a função passada por prop
            onClose();
          }}
          style={{
            padding: "0.5rem 1rem",
            border: "none",
            borderRadius: 6,
            background: "#4f46e5",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          Salvar
        </button>
      </div>
    </div>
  </div>
);

export const ModalBackupOptions = ({ config, onClose, onSaved }) => {
  const databases = Array.isArray(config.databases) ? config.databases : [];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "#1a1a1a",
          padding: "2rem",
          borderRadius: "10px",
          maxWidth: "760px",
          width: "95%",
          color: "#fff",
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <h2>Configurações de Backup - {config.userEmail}</h2>

        {databases.length === 0 ? (
          <p style={{ color: "#aaa", marginTop: "1rem" }}>
            Nenhuma base encontrada.
          </p>
        ) : (
          databases.map((db) => (
            <BackupDatabaseConfigCard
              key={`${db.IS_MAIN_DATABASE}-${db.ID_BACKUP_DATABASE}`}
              config={config}
              db={db}
              onSaved={onSaved}
            />
          ))
        )}

        <div style={{ textAlign: "right", marginTop: "1rem" }}>
          <button
            onClick={onClose}
            style={{
              margin: "0.5rem",
              borderRadius: "var(--swal2-cancel-button-border-radius)",
              background: "initial",
              backgroundColor: "var(--swal2-cancel-button-background-color)",
              boxShadow: "var(--swal2-cancel-button-box-shadow)",
              color: "var(--swal2-cancel-button-color)",
              padding: "0.5rem 1rem",
              border: "none",
              cursor: "pointer",
            }}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

const BackupDatabaseConfigCard = ({ config, db, onSaved }) => {
  const { token } = useContext(AuthContext);
  const normalizeWeekDays = (value) => {
    if (!value) return [];

    return String(value)
      .split(",")
      .map((d) => d.trim())
      .filter(Boolean);
  };

  const [weekDays, setWeekDays] = useState(normalizeWeekDays(db.WEEK_DAYS));
  const [hours, setHours] = useState(
    db.HOURS ? String(db.HOURS).split(",").filter(Boolean) : [],
  );
  const [isChanged, setIsChanged] = useState(false);
  const [showHourInput, setShowHourInput] = useState(false);
  const [newHour, setNewHour] = useState("");
  const [editingHourIndex, setEditingHourIndex] = useState(null);
  const [editingHourValue, setEditingHourValue] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);

  const dayOptions = [
    { value: "monday", label: "Seg" },
    { value: "tuesday", label: "Ter" },
    { value: "wednesday", label: "Qua" },
    { value: "thursday", label: "Qui" },
    { value: "friday", label: "Sex" },
    { value: "saturday", label: "Sáb" },
    { value: "sunday", label: "Dom" },
  ];

  const toggleDay = (day) => {
    const updated = weekDays.includes(day)
      ? weekDays.filter((d) => d !== day)
      : [...weekDays, day];

    setWeekDays(updated);
    setIsChanged(true);
  };

  const addHour = () => {
    if (newHour.trim()) {
      setHours([...hours, newHour.trim()]);
      setNewHour("");
      setShowHourInput(false);
      setIsChanged(true);
    }
  };

  const updateHour = (index, value) => {
    const updated = [...hours];
    updated[index] = value;
    setHours(updated);
    setIsChanged(true);
  };

  const deleteHour = (index) => {
    const updated = [...hours];
    updated.splice(index, 1);
    setHours(updated);
    setIsChanged(true);
  };

  const hasConfig =
    weekDays.length > 0 &&
    hours.length > 0 &&
    Number(db.TIMES_PER_DAY || 0) > 0;

  const handleSave = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/salvar_backup_settings_v2.php`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: config.userId,
            id_backup_database: Number(db.ID_BACKUP_DATABASE || 0),
            is_main_database: Number(db.IS_MAIN_DATABASE || 0),
            week_days: weekDays.join(","),
            times_per_day: hours.length,
            hours: hours.join(","),
          }),
        },
      );

      const data = await res.json();

      if (data.sucesso) {
        Swal.fire({
          title: "Sucesso",
          text: data.mensagem,
          icon: "success",
          theme: "dark",
        });

        setIsChanged(false);

        if (typeof onSaved === "function") {
          onSaved();
        }
      } else {
        Swal.fire({
          title: "Erro",
          text: data.erro || "Erro ao salvar configurações",
          icon: "error",
          theme: "dark",
        });
      }
    } catch (err) {
      Swal.fire({
        title: "Erro",
        text: "Erro de conexão com o servidor: " + err,
        icon: "error",
        theme: "dark",
      });
    }
  };

  return (
    <div
      style={{
        marginTop: "1.5rem",
        padding: "1rem",
        background: "#111",
        border: "1px solid #333",
        borderRadius: "10px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <h3 style={{ margin: 0 }}>{db.DATABASE_TYPE}</h3>

        <span style={{ color: "#aaa", fontSize: "0.9rem" }}>
          Pasta: <strong>{db.BACKUP_FOLDER}</strong>
        </span>
      </div>

      {!hasConfig && (
        <p style={{ color: "#facc15", marginBottom: "1rem" }}>
          ⚠️ Falta criar configuração de backup para esta base.
        </p>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {dayOptions.map((day) => (
          <button
            key={day.value}
            onClick={() => toggleDay(day.value)}
            style={{
              padding: "0.45rem 0.75rem",
              borderRadius: 6,
              border: "none",
              cursor: "pointer",
              background: weekDays.includes(day.value) ? "#4f46e5" : "#2a2a2a",
              color: "#fff",
            }}
          >
            {day.label}
          </button>
        ))}
      </div>

      {showHourInput && (
        <div style={{ marginTop: "1rem" }}>
          <input
            type="time"
            value={newHour}
            onChange={(e) => setNewHour(e.target.value)}
            style={{
              backgroundColor: "#2a2a2a",
              color: "#fff",
              border: "1px solid #444",
              borderRadius: "6px",
              padding: "0.5rem 0.75rem",
              marginRight: "0.5rem",
            }}
          />

          <button
            onClick={addHour}
            style={{
              background: "#22c55e",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "0.5rem 1rem",
              cursor: "pointer",
            }}
          >
            Confirmar
          </button>
        </div>
      )}

      <table
        style={{ width: "100%", marginTop: "1rem", borderCollapse: "collapse" }}
      >
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: 10 }}>Horário</th>
            <th style={{ textAlign: "left", padding: 10 }}>Ações</th>
          </tr>
        </thead>

        <tbody>
          {hours.length === 0 ? (
            <tr>
              <td colSpan="2" style={{ padding: 10, color: "#aaa" }}>
                Nenhum horário cadastrado.
              </td>
            </tr>
          ) : (
            hours.map((hour, i) => (
              <tr key={`${hour}-${i}`}>
                <td style={{ padding: 10 }}>{hour}</td>
                <td style={{ padding: 10 }}>
                  <button
                    onClick={() => {
                      setEditingHourIndex(i);
                      setEditingHourValue(hour);
                      setShowEditModal(true);
                    }}
                    style={{
                      background: "#0084c2",
                      color: "white",
                      padding: "0.45rem 1rem",
                      borderRadius: 8,
                      cursor: "pointer",
                      border: "none",
                      marginRight: 5,
                    }}
                  >
                    Editar
                  </button>

                  <button
                    onClick={() => deleteHour(i)}
                    style={{
                      background: "#dc2626",
                      color: "white",
                      padding: "0.45rem 1rem",
                      borderRadius: 8,
                      cursor: "pointer",
                      border: "none",
                    }}
                  >
                    Excluir
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div style={{ textAlign: "right", marginTop: "1rem" }}>
        <button
          onClick={() => setShowHourInput(true)}
          style={{
            margin: "0.5rem",
            background: "#22c55e",
            color: "white",
            border: "none",
            borderRadius: "4px",
            padding: "0.4rem 0.8rem",
            cursor: "pointer",
          }}
        >
          + Adicionar Horário
        </button>

        <button
          disabled={!isChanged}
          onClick={handleSave}
          style={{
            margin: "0.5rem",
            background: isChanged ? "#4f46e5" : "#999",
            color: "white",
            border: "none",
            borderRadius: "6px",
            padding: "0.5rem 1rem",
            cursor: isChanged ? "pointer" : "not-allowed",
          }}
        >
          Salvar Configurações
        </button>
      </div>

      {showEditModal && (
        <ModalEditHour
          value={editingHourValue}
          onConfirm={(val) => setEditingHourValue(val)}
          onClose={() => setShowEditModal(false)}
          onSave={() => updateHour(editingHourIndex, editingHourValue)}
        />
      )}
    </div>
  );
};

const Wrapper = styled.div`
  min-height: 100dvh;
  background: #0f0f0f;
  color: #fff;
  padding-top: 60px; /* altura do header */
  padding-left: 240px; /* espaço da sidebar no desktop */
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;

  @media (max-width: 760px) {
    padding-left: 0;
    overflow-x: visible;
  }
`;

const Main = styled.main`
  display: flex;
  flex-direction: column;
  padding-bottom: 2rem;

  /* CRUCIAL em layouts flex: permite encolher e não forçar largura */
  min-width: 0;
`;

const TableViewport = styled.div`
  width: 100%;
  overflow-x: visible;

  @media (max-width: 1380px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  border-radius: 8px;
`;

const Content = styled.div`
  padding: 2rem;
`;

const Section = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const Title = styled.h3`
  margin-bottom: 1.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 0.75rem;
    border-bottom: 1px solid #333;
    text-align: left;
    white-space: normal;
  }
  th {
    color: #ccc;
  }
  td {
    color: #eee;
  }

  @media (max-width: 1380px) {
    min-width: 1100px;
    width: max-content;
    th,
    td {
      white-space: nowrap;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const Input = styled.input`
  background: #2a2a2a;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem;
`;

const Select = styled.select`
  background: #2a2a2a;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem;
`;

const Button = styled.button`
  background: #4f46e5;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem;
  cursor: pointer;
  &:hover {
    background: #3730a3;
  }
`;
